// AI Agents & Knowledge Sets Manager (WP-10).
// Advanced multi-agent orchestration, knowledge base ingestion, routing rules, and multi-provider keys vault.

import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { StatCard } from '../components/ui/StatCard';
import { PillTabs } from '../components/ui/PillTabs';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import Toggle from '../components/ui/Toggle';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { FallbackWarningBanner } from '../components/ai/FallbackWarningBanner';
import { AgentModal } from '../components/ai/AgentModal';
import { KnowledgeSetModal } from '../components/ai/KnowledgeSetModal';
import { KnowledgeSourcesDrawer } from '../components/ai/KnowledgeSourcesDrawer';
import { RoutingRuleModal } from '../components/ai/RoutingRuleModal';
import { ProviderCard } from '../components/ai/ProviderCard';
import { useLabels } from '../lib/crm';
import {
  AI_PROVIDERS,
  listAiAgents,
  deleteAiAgent,
  listKnowledgeSets,
  deleteKnowledgeSet,
  getAiConfig,
  saveAiConfig,
  getAiProviderKeys,
  saveAiProviderKeys,
  type AiAgentDoc,
  type KnowledgeSetDoc,
  type AiConfigDoc,
  type AiProvider,
  type AiProviderInfo,
  type AiRoutingRule,
} from '../lib/aiAgents';

type ActiveTab = 'agents' | 'knowledge' | 'routing' | 'providers';

export default function AiAgents() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { labels } = useLabels();
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState<ActiveTab>('agents');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data states
  const [agents, setAgents] = useState<AiAgentDoc[]>([]);
  const [knowledgeSets, setKnowledgeSets] = useState<KnowledgeSetDoc[]>([]);
  const [aiConfig, setAiConfig] = useState<AiConfigDoc>({
    enabled: false,
    defaultAgentId: null,
    enabledAgentIds: [],
    routingRules: [],
  });
  const [providers, setProviders] = useState<Record<AiProvider, AiProviderInfo>>({
    anthropic: { configured: false, keyMasked: null },
    openai: { configured: false, keyMasked: null },
    deepseek: { configured: false, keyMasked: null },
    gemini: { configured: false, keyMasked: null },
  });

  // Draft provider keys inputs
  const [draftKeys, setDraftKeys] = useState<Record<AiProvider, string>>({
    anthropic: '',
    openai: '',
    deepseek: '',
    gemini: '',
  });

  // Modal / Drawer states
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AiAgentDoc | null>(null);

  const [knowledgeSetModalOpen, setKnowledgeSetModalOpen] = useState(false);
  const [editingKnowledgeSet, setEditingKnowledgeSet] = useState<KnowledgeSetDoc | null>(null);

  const [sourcesDrawerOpen, setSourcesDrawerOpen] = useState(false);
  const [activeKnowledgeSetId, setActiveKnowledgeSetId] = useState<string | null>(null);

  const [ruleModalOpen, setRuleModalOpen] = useState(false);

  // Status banners / busy indicators
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);
  const [keysSaved, setKeysSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  // Load all data
  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const [agentsData, setsData, configData, keysData] = await Promise.all([
        listAiAgents(),
        listKnowledgeSets(),
        getAiConfig(),
        getAiProviderKeys(),
      ]);

      if (!mountedRef.current) return;
      setAgents(agentsData);
      setKnowledgeSets(setsData);
      setAiConfig(configData);
      setProviders(keysData);
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err.message || 'Failed to load AI data');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadData();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isAnyProviderConfigured = useMemo(() => {
    return Object.values(providers).some((p) => p.configured);
  }, [providers]);

  const configuredProvidersCount = useMemo(() => {
    return Object.values(providers).filter((p) => p.configured).length;
  }, [providers]);

  const activeAgentsCount = useMemo(() => {
    return agents.filter((a) => a.status === 'active').length;
  }, [agents]);

  // Agent Actions
  const handleOpenCreateAgent = () => {
    setEditingAgent(null);
    setAgentModalOpen(true);
  };

  const handleOpenEditAgent = (agent: AiAgentDoc) => {
    setEditingAgent(agent);
    setAgentModalOpen(true);
  };

  const handleDeleteAgent = async (agent: AiAgentDoc) => {
    if (!(await confirm({ title: t('common.areYouSure'), body: t('ai.agents.deleteConfirm'), danger: true }))) return;
    try {
      await deleteAiAgent(agent.id);
      loadData(true);
    } catch (err: any) {
      setError(err.message || 'Failed to delete agent');
    }
  };

  // Knowledge Set Actions
  const handleOpenCreateSet = () => {
    setEditingKnowledgeSet(null);
    setKnowledgeSetModalOpen(true);
  };

  const handleOpenEditSet = (set: KnowledgeSetDoc) => {
    setEditingKnowledgeSet(set);
    setKnowledgeSetModalOpen(true);
  };

  const handleDeleteSet = async (setId: string) => {
    if (!(await confirm({ title: t('common.areYouSure'), body: t('ai.knowledge.deleteConfirm'), danger: true }))) return;
    try {
      await deleteKnowledgeSet(setId);
      loadData(true);
    } catch (err: any) {
      setError(err.message || 'Failed to delete knowledge set');
    }
  };

  const handleOpenManageSources = (setId: string) => {
    setActiveKnowledgeSetId(setId);
    setSourcesDrawerOpen(true);
  };

  // Routing Config Actions
  const handleSaveRoutingConfig = async () => {
    setSavingConfig(true);
    setConfigSaved(false);
    setError(null);
    try {
      await saveAiConfig(aiConfig);
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleAddRoutingRule = (newRule: AiRoutingRule) => {
    setAiConfig((prev) => ({
      ...prev,
      routingRules: [...prev.routingRules, newRule],
    }));
  };

  const handleDeleteRoutingRule = (ruleId: string) => {
    setAiConfig((prev) => ({
      ...prev,
      routingRules: prev.routingRules.filter((r) => r.id !== ruleId),
    }));
  };

  // Provider Keys Actions
  const handleSaveKeys = async () => {
    setSavingKeys(true);
    setKeysSaved(false);
    setError(null);
    try {
      const payload: Record<string, string> = {};
      if (draftKeys.anthropic.trim()) payload.anthropicKey = draftKeys.anthropic.trim();
      if (draftKeys.openai.trim()) payload.openaiKey = draftKeys.openai.trim();
      if (draftKeys.deepseek.trim()) payload.deepseekKey = draftKeys.deepseek.trim();
      if (draftKeys.gemini.trim()) payload.geminiKey = draftKeys.gemini.trim();

      if (Object.keys(payload).length > 0) {
        await saveAiProviderKeys(payload);
        setDraftKeys({
          anthropic: '',
          openai: '',
          deepseek: '',
          gemini: '',
        });
        const updated = await getAiProviderKeys();
        setProviders(updated);
      }
      setKeysSaved(true);
      setTimeout(() => setKeysSaved(false), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to save provider keys');
    } finally {
      setSavingKeys(false);
    }
  };

  const knowledgeSetLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const ks of knowledgeSets) {
      map.set(ks.id, ks.name);
    }
    return map;
  }, [knowledgeSets]);

  const agentLookup = useMemo(() => {
    const map = new Map<string, AiAgentDoc>();
    for (const a of agents) {
      map.set(a.id, a);
    }
    return map;
  }, [agents]);

  const labelLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of labels) {
      map.set(l.id, l.name);
    }
    return map;
  }, [labels]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Header with breadcrumbs */}
      <div>
        <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-ink-3 dark:text-slate-400">
          <button
            type="button"
            onClick={() => navigate('/bots')}
            className="hover:text-ink dark:hover:text-white"
          >
            {t('bot.title')}
          </button>
          <span>/</span>
          <span className="text-ink dark:text-white">{t('ai.title')}</span>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-ink dark:text-white">
              {t('ai.title')}
            </h1>
            <p className="text-xs text-ink-3 dark:text-slate-400">
              {t('ai.subtitle')}
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => loadData(true)}
            loading={refreshing}
          >
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 p-4 text-xs text-danger dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-400">
          {error}
        </div>
      )}

      {/* Fallback Warning Banner if no API keys are configured */}
      {!isAnyProviderConfigured && (
        <FallbackWarningBanner onConfigureClick={() => setActiveTab('providers')} />
      )}

      {/* 4 StatCards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label={t('ai.stats.totalAgents')}
          value={agents.length}
          variant="indigo"
        />
        <StatCard
          label={t('ai.stats.activeAgents')}
          value={activeAgentsCount}
          variant="green"
        />
        <StatCard
          label={t('ai.stats.knowledgeSets')}
          value={knowledgeSets.length}
          variant="sky"
        />
        <StatCard
          label={t('ai.stats.configuredProviders')}
          value={`${configuredProvidersCount} / ${AI_PROVIDERS.length}`}
          variant={configuredProvidersCount > 0 ? 'amber' : 'slate'}
        />
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line pb-2 dark:border-slate-800">
        <PillTabs
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as ActiveTab)}
          tabs={[
            { id: 'agents', label: t('ai.tabs.agents'), count: agents.length },
            { id: 'knowledge', label: t('ai.tabs.knowledge'), count: knowledgeSets.length },
            { id: 'routing', label: t('ai.tabs.routing') },
            { id: 'providers', label: t('ai.tabs.providers'), count: configuredProvidersCount },
          ]}
        />
      </div>

      {/* Tab 1: Agents */}
      {activeTab === 'agents' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-ink dark:text-white">
              {t('ai.tabs.agents')} ({agents.length})
            </h2>
            <Button size="sm" variant="primary" onClick={handleOpenCreateAgent}>
              + {t('ai.agents.create')}
            </Button>
          </div>

          {agents.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line p-12 text-center dark:border-slate-800">
              <p className="text-sm font-semibold text-ink dark:text-white">
                {t('ai.agents.empty')}
              </p>
              <p className="mt-1 text-xs text-ink-3 dark:text-slate-400">
                {t('ai.agents.emptyDesc')}
              </p>
              <div className="mt-4">
                <Button size="sm" variant="primary" onClick={handleOpenCreateAgent}>
                  + {t('ai.agents.create')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className="flex flex-col justify-between rounded-xl border border-line bg-surface p-5 shadow-sm transition hover:shadow dark:border-slate-800 dark:bg-slate-850"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-bold text-ink dark:text-white">
                            {agent.name}
                          </h3>
                          <Badge variant={agent.status === 'active' ? 'success' : 'slate'}>
                            {agent.status === 'active' ? t('ai.agents.active') : t('ai.agents.inactive')}
                          </Badge>
                        </div>
                        <span className="text-xs font-mono text-ink-3 dark:text-slate-400">
                          @{agent.slug}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleOpenEditAgent(agent)}
                        >
                          {t('ai.agents.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteAgent(agent)}
                        >
                          {t('ai.agents.delete')}
                        </Button>
                      </div>
                    </div>

                    {agent.description && (
                      <p className="text-xs text-ink-2 dark:text-slate-300">
                        {agent.description}
                      </p>
                    )}

                    <div className="rounded-lg bg-surface-subtle p-3 dark:bg-slate-900/60">
                      <span className="block text-[11px] font-semibold text-ink-3 dark:text-slate-400">
                        {t('ai.agents.systemPrompt')}:
                      </span>
                      <p className="mt-1 line-clamp-3 text-xs text-ink-2 dark:text-slate-300">
                        {agent.systemPrompt}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
                      <span className="rounded-md bg-brand-50 px-2 py-1 font-semibold text-brand dark:bg-brand-950/50 dark:text-brand-tint">
                        {agent.provider.toUpperCase()} · {agent.model}
                      </span>

                      {agent.knowledgeSetId && knowledgeSetLookup.has(agent.knowledgeSetId) ? (
                        <span className="rounded-md bg-sky-50 px-2 py-1 font-semibold text-sky-700 dark:bg-sky-950/50 dark:text-sky-400">
                          📚 {knowledgeSetLookup.get(agent.knowledgeSetId)}
                        </span>
                      ) : (
                        <span className="rounded-md bg-slate-100 px-2 py-1 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                          {t('ai.agents.noKnowledgeSet')}
                        </span>
                      )}

                      <span className="text-[11px] text-ink-3 dark:text-slate-400">
                        Temp: {agent.temperature} · Max: {agent.maxTokens || 600}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Knowledge Sets */}
      {activeTab === 'knowledge' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-ink dark:text-white">
              {t('ai.tabs.knowledge')} ({knowledgeSets.length})
            </h2>
            <Button size="sm" variant="primary" onClick={handleOpenCreateSet}>
              + {t('ai.knowledge.create')}
            </Button>
          </div>

          {knowledgeSets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line p-12 text-center dark:border-slate-800">
              <p className="text-sm font-semibold text-ink dark:text-white">
                {t('ai.knowledge.empty')}
              </p>
              <p className="mt-1 text-xs text-ink-3 dark:text-slate-400">
                {t('ai.knowledge.emptyDesc')}
              </p>
              <div className="mt-4">
                <Button size="sm" variant="primary" onClick={handleOpenCreateSet}>
                  + {t('ai.knowledge.create')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {knowledgeSets.map((ks) => (
                <div
                  key={ks.id}
                  className="flex flex-col justify-between rounded-xl border border-line bg-surface p-5 shadow-sm transition hover:shadow dark:border-slate-800 dark:bg-slate-850"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="text-base font-bold text-ink dark:text-white">{ks.name}</h3>
                        {ks.description && (
                          <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">
                            {ks.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleOpenEditSet(ks)}
                        >
                          {t('ai.knowledge.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteSet(ks.id)}
                        >
                          {t('ai.knowledge.delete')}
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 pt-2 text-xs">
                      <span className="rounded-md bg-brand-50 px-2 py-1 font-semibold text-brand dark:bg-brand-950/50 dark:text-brand-tint">
                        {ks.totalChunks || 0} {t('ai.knowledge.totalChunks')}
                      </span>
                      <span className="rounded-md bg-slate-100 px-2 py-1 font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {ks.sourceCount ?? (ks.sources?.length || 0)} {t('ai.knowledge.sources')}
                      </span>
                      <span className="text-[11px] text-ink-3 dark:text-slate-400">
                        {ks.lastTrainedAt
                          ? `${t('ai.knowledge.lastTrained')}: ${new Date(ks.lastTrainedAt).toLocaleDateString()}`
                          : t('ai.knowledge.neverTrained')}
                      </span>
                    </div>

                    <div className="pt-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleOpenManageSources(ks.id)}
                        className="w-full"
                      >
                        📂 {t('ai.knowledge.manageSources')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Routing & Automation */}
      {activeTab === 'routing' && (
        <div className="space-y-6">
          {/* Master enablement */}
          <div className="rounded-xl border border-line bg-surface p-5 shadow-sm dark:border-slate-800 dark:bg-slate-850">
            <div className="flex items-center justify-between">
              <div>
                <b className="block text-sm text-ink dark:text-white">{t('ai.routing.enableMaster')}</b>
                <span className="text-xs text-ink-3 dark:text-slate-400">
                  {t('ai.routing.enableMasterNote')}
                </span>
              </div>
              <Toggle
                on={aiConfig.enabled}
                onChange={(v) => setAiConfig((prev) => ({ ...prev, enabled: v }))}
                label={t('ai.routing.enableMaster')}
              />
            </div>
          </div>

          {/* Default Agent Picker */}
          <div className="rounded-xl border border-line bg-surface p-5 shadow-sm dark:border-slate-800 dark:bg-slate-850">
            <label className="block text-sm font-bold text-ink dark:text-white">
              {t('ai.routing.defaultAgent')}
            </label>
            <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">
              {t('ai.routing.desc')}
            </p>
            <select
              value={aiConfig.defaultAgentId || ''}
              onChange={(e) =>
                setAiConfig((prev) => ({
                  ...prev,
                  defaultAgentId: e.target.value || null,
                }))
              }
              className="mt-3 w-full max-w-md rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            >
              <option value="">{t('ai.routing.defaultAgentNone')}</option>
              {agents
                .filter((a) => a.status === 'active')
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} (@{a.slug}) — {a.provider}
                  </option>
                ))}
            </select>
          </div>

          {/* Routing Rules Table */}
          <div className="rounded-xl border border-line bg-surface p-5 shadow-sm dark:border-slate-800 dark:bg-slate-850">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-ink dark:text-white">
                  {t('ai.routing.rulesTitle')} ({aiConfig.routingRules.length})
                </h3>
                <p className="text-xs text-ink-3 dark:text-slate-400">
                  {t('ai.routing.rulesDesc')}
                </p>
              </div>
              <Button size="sm" variant="primary" onClick={() => setRuleModalOpen(true)}>
                + {t('ai.routing.addRule')}
              </Button>
            </div>

            {aiConfig.routingRules.length === 0 ? (
              <div className="rounded-xl border border-dashed border-line p-8 text-center dark:border-slate-800">
                <p className="text-xs font-semibold text-ink dark:text-white">
                  {t('ai.routing.emptyRules')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-start text-xs">
                  <thead>
                    <tr className="border-b border-line text-ink-3 dark:border-slate-800 dark:text-slate-400">
                      <th className="py-2 pe-4 ps-2 text-start font-semibold">
                        {t('ai.routing.priority')}
                      </th>
                      <th className="py-2 px-4 text-start font-semibold">
                        {t('ai.routing.ruleType')}
                      </th>
                      <th className="py-2 px-4 text-start font-semibold">Condition & Match</th>
                      <th className="py-2 px-4 text-start font-semibold">
                        {t('ai.routing.targetAgent')}
                      </th>
                      <th className="py-2 pe-2 ps-4 text-end font-semibold">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line dark:divide-slate-800">
                    {aiConfig.routingRules.map((rule, idx) => {
                      const targetAgent = agentLookup.get(rule.agentId);
                      return (
                        <tr key={rule.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="py-3 pe-4 ps-2 font-mono font-bold text-ink-2 dark:text-slate-300">
                            #{idx + 1}
                          </td>
                          <td className="py-3 px-4">
                            <span className="rounded bg-slate-100 px-2 py-0.5 font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                              {rule.type === 'keyword'
                                ? t('ai.routing.matchKeyword')
                                : t('ai.routing.matchLabel')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-ink dark:text-white">
                            {rule.type === 'keyword' ? (
                              <span>
                                <span className="font-semibold text-ink-3">
                                  {rule.matchType === 'exact'
                                    ? t('ai.routing.matchExact')
                                    : t('ai.routing.matchContains')}
                                  :
                                </span>{' '}
                                <code className="rounded bg-surface-subtle px-1.5 py-0.5 font-mono text-brand dark:bg-slate-900">
                                  "{rule.value}"
                                </code>
                              </span>
                            ) : (
                              <span>
                                <span className="font-semibold text-ink-3">
                                  {t('ai.routing.matchLabel')}:
                                </span>{' '}
                                <span className="font-semibold text-ink dark:text-white">
                                  {labelLookup.get(rule.value || '') || rule.value}
                                </span>
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {targetAgent ? (
                              <span className="font-semibold text-ink dark:text-white">
                                {targetAgent.name} (@{targetAgent.slug})
                              </span>
                            ) : (
                              <span className="text-danger">Deleted agent ({rule.agentId})</span>
                            )}
                          </td>
                          <td className="py-3 pe-2 ps-4 text-end">
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDeleteRoutingRule(rule.id)}
                            >
                              {t('common.delete')}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-6 flex items-center justify-between border-t border-line pt-4 dark:border-slate-800">
              <span className="text-xs text-ink-3">
                {configSaved && <span className="font-bold text-success">{t('ai.routing.saved')}</span>}
              </span>
              <Button
                size="md"
                variant="primary"
                onClick={handleSaveRoutingConfig}
                loading={savingConfig}
              >
                {t('ai.routing.saveConfig')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: Providers & Keys */}
      {activeTab === 'providers' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-base font-bold text-ink dark:text-white">
              {t('ai.providers.title')}
            </h2>
            <p className="text-xs text-ink-3 dark:text-slate-400">
              {t('ai.providers.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {AI_PROVIDERS.map((p) => (
              <ProviderCard
                key={p}
                provider={p}
                configured={providers[p]?.configured || false}
                keyMasked={providers[p]?.keyMasked || null}
                keyValue={draftKeys[p]}
                onChangeKey={(val) => setDraftKeys((prev) => ({ ...prev, [p]: val }))}
              />
            ))}
          </div>

          <div className="flex items-center justify-between rounded-xl border border-line bg-surface p-4 dark:border-slate-800 dark:bg-slate-850">
            <span className="text-xs">
              {keysSaved ? (
                <span className="font-bold text-success">{t('ai.providers.keysSaved')}</span>
              ) : (
                <span className="text-ink-3 dark:text-slate-400">{t('ai.footnote')}</span>
              )}
            </span>
            <Button
              size="md"
              variant="primary"
              onClick={handleSaveKeys}
              loading={savingKeys}
            >
              {t('ai.providers.saveKeys')}
            </Button>
          </div>
        </div>
      )}

      {/* Modals & Drawers */}
      <AgentModal
        open={agentModalOpen}
        agent={editingAgent}
        knowledgeSets={knowledgeSets}
        onClose={() => setAgentModalOpen(false)}
        onSaved={() => loadData(true)}
      />

      <KnowledgeSetModal
        open={knowledgeSetModalOpen}
        set={editingKnowledgeSet}
        onClose={() => setKnowledgeSetModalOpen(false)}
        onSaved={() => loadData(true)}
      />

      <KnowledgeSourcesDrawer
        open={sourcesDrawerOpen}
        knowledgeSetId={activeKnowledgeSetId}
        onClose={() => setSourcesDrawerOpen(false)}
        onUpdated={() => loadData(true)}
      />

      <RoutingRuleModal
        open={ruleModalOpen}
        agents={agents.filter((a) => a.status === 'active')}
        onClose={() => setRuleModalOpen(false)}
        onSaveRule={handleAddRoutingRule}
      />
    </div>
  );
}
